import { Spinner } from "components";
import React, { useState, useEffect } from "react";
import Chart from "react-apexcharts";

const RevenueChart = ({
  percentageData = [],
  rawData = [],
  categories = [],
}) => {
  const [fadeIn, setFadeIn] = useState(false);
  const [chartReady, setChartReady] = useState(false);

  useEffect(() => {
    setChartReady(false);
    const timer = setTimeout(() => {
      setChartReady(true);
      setTimeout(() => {
        setFadeIn(true);
      }, 100);
    }, 500);

    return () => clearTimeout(timer);
  }, [percentageData, categories]);

  if (!chartReady) {
    return (
      <div className="w-full h-full flex-1 flex items-center justify-center p-4 min-h-96">
        <Spinner size="large" color="#19a8a6" />
      </div>
    );
  }

  const safePercentageData =
    Array.isArray(percentageData) && percentageData.length > 0
      ? percentageData
      : [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
  const safeRawData =
    Array.isArray(rawData) && rawData.length > 0
      ? rawData
      : [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
  const defaultCategories = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const safeCategories =
    Array.isArray(categories) && categories.length > 0
      ? categories.slice(0, safePercentageData.length)
      : defaultCategories.slice(0, safePercentageData.length);

  const maxPercentage = Math.max(...safePercentageData, 0);
  const yAxisMax =
    maxPercentage === 0
      ? 100
      : Math.max(20, Math.ceil(maxPercentage / 10) * 10);
  const tickAmount = 5;

  const options = {
    chart: {
      height: 350,
      type: "line",
      toolbar: { show: false },
      fontFamily: "inherit",
      background: "transparent",
      animations: { enabled: false },
    },
    stroke: {
      width: [0, 3],
      curve: "straight",
    },
    plotOptions: {
      bar: {
        columnWidth: "60%",
        opacity: 0.7,
      },
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories: safeCategories,
      labels: {
        style: {
          colors: "#737373",
          fontSize: "12px",
        },
      },
      axisBorder: { show: true, color: "#737373" },
      axisTicks: { show: true, color: "#737373" },
    },
    yaxis: {
      min: 0,
      max: yAxisMax,
      tickAmount: tickAmount,
      labels: {
        formatter: (value) => `${Math.round(value)}%`,
        style: {
          colors: "#737373",
          fontSize: "12px",
        },
      },
      axisBorder: { show: true, color: "#737373" },
      axisTicks: { show: true, color: "#737373" },
    },
    grid: { show: false },
    tooltip: {
      enabled: true,
      custom: ({ series, dataPointIndex }) => {
        const percentage = series[0][dataPointIndex];
        const revenue = safeRawData[dataPointIndex];
        return `
          <div class="p-2 bg-gray-800 text-white rounded shadow">
            <div>Month: ${safeCategories[dataPointIndex]}</div>
            <div>Revenue: ${percentage}% of total</div>
            <div>Amount: $${revenue.toFixed(2)}</div>
          </div>
        `;
      },
    },
    colors: ["#19a8a6", "#fab54e"],
    legend: {
      show: true,
      labels: { colors: "#737373" },
    },
  };

  const series = [
    { name: "Revenue (Bar)", type: "bar", data: safePercentageData },
    { name: "Revenue (Line)", type: "line", data: safePercentageData },
  ];

  return (
    <div
      className={`w-full h-full flex-1 transition-opacity duration-300 ease-in-out ${
        fadeIn ? "opacity-100" : "opacity-0"
      }`}
    >
      <Chart
        options={options}
        series={series}
        type="line"
        width="100%"
        height="100%"
      />
    </div>
  );
};

export default React.memo(RevenueChart);
